import { useState } from "react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/adminApi";
import { FEE_SOURCE, money, roomFeeText } from "@/studio/ui";

const DiscountForm = ({ project, onProject }) => {
  const d = project.discount;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "percent", value: "", reason: "" });
  const [error, setError] = useState("");

  const apply = async () => {
    const value = Number(form.value);
    if (!(value > 0)) return setError("Enter a discount greater than zero");
    if (form.type === "percent" && value > 100) return setError("A percentage cannot exceed 100%");
    if (!form.reason.trim()) return setError("A reason or promo name is required");
    try {
      const { data } = await adminApi.put(`/studio/projects/${project.id}/discount`, { ...form, value });
      onProject(data);
      setOpen(false);
      setError("");
      toast.success("Discount applied");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };
  const remove = async () => {
    const { data } = await adminApi.delete(`/studio/projects/${project.id}/discount`);
    onProject(data);
    toast.success("Discount removed");
  };

  if (d) {
    return (
      <div className="st-row" style={{ paddingBottom: 0 }}>
        <span className="st-small st-muted">Promo: {d.type === "percent" ? `${d.value}%` : money(d.value)} · {d.reason}</span>
        <button className="st-btn st-btn--quiet st-btn--sm st-btn--danger" onClick={remove} data-testid="discount-remove-button">Remove</button>
      </div>
    );
  }
  if (!open) return <button className="st-btn st-btn--quiet st-btn--sm" style={{ marginTop: 8, paddingLeft: 0 }} onClick={() => setOpen(true)} data-testid="discount-open-button">+ Promotional discount (staff only)</button>;
  return (
    <div style={{ display: "grid", gap: 10, marginTop: 14 }} data-testid="discount-form">
      <div style={{ display: "flex", gap: 8 }}>
        <div className="st-seg">
          {[["percent", "%"], ["amount", "$"]].map(([v, l]) => (
            <button key={v} className={form.type === v ? "is-on" : ""} onClick={() => setForm((f) => ({ ...f, type: v }))} data-testid={`discount-type-${v}`}>{l}</button>
          ))}
        </div>
        <input className="st-input" type="number" min="0" placeholder={form.type === "percent" ? "10" : "200"} value={form.value} onChange={(e) => { const value = e.target.value; setForm((f) => ({ ...f, value })); }} data-testid="discount-value-input" />
      </div>
      <input className="st-input" placeholder="Reason or promo name (required)" value={form.reason} maxLength={120} onChange={(e) => { const reason = e.target.value; setForm((f) => ({ ...f, reason })); }} data-testid="discount-reason-input" />
      {error && <span className="st-error" data-testid="discount-error">{error}</span>}
      <div style={{ display: "flex", gap: 8 }}>
        <button className="st-btn st-btn--sm" onClick={apply} data-testid="discount-apply-button">Apply discount</button>
        <button className="st-btn st-btn--ghost st-btn--sm" onClick={() => { setOpen(false); setError(""); }} data-testid="discount-cancel-button">Cancel</button>
      </div>
    </div>
  );
};

export const PricingPanel = ({ project, onProject }) => {
  const s = project.pricing_snapshot;
  const [busy, setBusy] = useState(false);
  const outdated = project.current_settings_version !== s.settings_version;

  const doReprice = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/studio/projects/${project.id}/reprice`);
      onProject(data);
      toast.success(`Repriced with settings v${data.pricing_snapshot.settings_version}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="st-panel" data-testid="pricing-panel">
      <div className="st-panel__head"><h2 className="st-h2">Design fee</h2><span className="st-tag st-tag--sage" data-testid="pricing-settings-version">Settings v{s.settings_version}</span></div>
      {s.rooms.length === 0 ? <p className="st-muted">Add rooms to see the pricing.</p> : (
        <ul className="st-list st-price" data-testid="pricing-breakdown">
          {s.rooms.map((r) => (
            <li key={r.room_id} data-testid={`pricing-room-${r.room_id}`}>
              <span>{r.label} <span className="st-muted st-small">· {FEE_SOURCE[r.fee_source]}</span></span>
              <span className={r.pending ? "st-tag st-tag--warn" : ""}>{roomFeeText(r)}</span>
            </li>
          ))}
          <li className="st-price__sum"><span>Subtotal</span><span data-testid="pricing-subtotal">{money(s.subtotal)}{s.pending ? " + pending" : ""}</span></li>
          {project.discount && <li><span>Discount <span className="st-muted st-small">· {project.discount.reason}</span></span><span data-testid="pricing-discount">−{money(s.discount_amount)}</span></li>}
          <li><span style={{ alignSelf: "center" }}>Total</span><span className="st-price__total" data-testid="pricing-total">{s.pending ? "Pending" : money(s.total)}</span></li>
        </ul>
      )}
      {s.pending && <p className="st-note st-note--warn" data-testid="pricing-pending-note">The total is pending until staff fees are entered for {s.rooms.filter((r) => r.pending).map((r) => r.label).join(", ")}.</p>}
      {project.has_signed_agreement ? <p className="st-small st-muted" data-testid="pricing-locked">Pricing locked by the signed agreement. Changes go through an amendment.</p> : <DiscountForm project={project} onProject={onProject} />}
      <p className="st-note" data-testid="pricing-furniture-note">Design fees cover design work only. Furniture budgets are separate and are not included in this total. No automatic multi-room discount is applied.</p>
      <div className="st-row" style={{ paddingBottom: 0, flexWrap: "wrap" }}>
        <span className="st-small st-muted">{outdated ? `Current settings are v${project.current_settings_version}. This project keeps its v${s.settings_version} prices until repriced.` : "Priced with the current settings."}</span>
        <button className="st-btn st-btn--ghost st-btn--sm" disabled={busy || project.has_signed_agreement} title={project.has_signed_agreement ? "Locked: agreement signed" : ""} onClick={doReprice} data-testid="pricing-reprice-button">Reprice with current settings</button>
      </div>
    </section>
  );
};
